// Social videos atomic sync bundle v3
(() => {
  const initSocialVideoCarousel = (root) => {
    if (!root || root.dataset.socialVideoReady === 'true') return;

    const track = root.querySelector('[data-social-video-track]');
    const items = Array.from(root.querySelectorAll('[data-social-video-item]'));
    const openButtons = Array.from(root.querySelectorAll('[data-social-video-open]'));
    const previousButton = root.querySelector('[data-carousel-previous]');
    const nextButton = root.querySelector('[data-carousel-next]');
    const modal = root.querySelector('[data-social-video-modal]');
    const modalHost = root.querySelector('[data-modal-video-host]');
    const modalPrevious = root.querySelector('[data-modal-previous]');
    const modalNext = root.querySelector('[data-modal-next]');
    const modalCloseButtons = Array.from(root.querySelectorAll('[data-modal-close]'));
    const modalCounter = root.querySelector('[data-modal-counter]');

    if (!track || !items.length || !modal || !modalHost) return;

    root.dataset.socialVideoReady = 'true';

    const playableIndices = items
      .map((item, index) => (item.querySelector('template[data-modal-template]') ? index : null))
      .filter((index) => index !== null);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayEnabled = root.dataset.autoplay === 'true';
    const autoplayInterval = Math.max(2000, parseFloat(root.dataset.autoplayInterval || '6') * 1000);

    let autoplayTimer = null;
    let modalIndex = playableIndices[0] ?? 0;
    let returnFocus = null;
    let previousBodyOverflow = '';

    const getScrollStep = () => {
      const firstItem = items[0];
      if (!firstItem) return track.clientWidth;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return firstItem.getBoundingClientRect().width + gap;
    };

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const moveByOne = (direction = 1, wrap = false) => {
      const step = getScrollStep();
      const maxScroll = getMaxScroll();
      if (step <= 0 || maxScroll <= 1) return;

      const currentIndex = Math.round(track.scrollLeft / step);
      let targetLeft = (currentIndex + direction) * step;

      if (direction > 0 && targetLeft > maxScroll + 1) {
        targetLeft = wrap ? 0 : maxScroll;
      } else if (direction < 0 && targetLeft < -1) {
        targetLeft = wrap ? maxScroll : 0;
      }

      targetLeft = Math.max(0, Math.min(targetLeft, maxScroll));
      track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    };

    const clearAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || items.length <= 1 || reducedMotion.matches || document.hidden || !modal.hidden) return;

      autoplayTimer = window.setTimeout(() => {
        moveByOne(1, true);
        scheduleAutoplay();
      }, autoplayInterval);
    };

    previousButton?.addEventListener('click', () => {
      moveByOne(-1, false);
      scheduleAutoplay();
    });

    nextButton?.addEventListener('click', () => {
      moveByOne(1, false);
      scheduleAutoplay();
    });

    let tracking = false;
    let horizontalDrag = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let suppressClickUntil = 0;

    const resetDrag = () => {
      tracking = false;
      horizontalDrag = false;
      pointerId = null;
      root.classList.remove('is-dragging');
    };

    track.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      clearAutoplay();
      tracking = true;
      horizontalDrag = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
    });

    track.addEventListener('pointermove', (event) => {
      if (!tracking || event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!horizontalDrag) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        if (Math.abs(deltaY) >= Math.abs(deltaX)) {
          resetDrag();
          scheduleAutoplay();
          return;
        }

        horizontalDrag = true;
        root.classList.add('is-dragging');
        if (track.setPointerCapture) track.setPointerCapture(event.pointerId);
      }

      track.scrollLeft = startScrollLeft - deltaX;
      event.preventDefault();
    });

    const finishDrag = (event) => {
      if (!tracking || event.pointerId !== pointerId) return;

      const wasDrag = horizontalDrag;
      if (wasDrag && track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      resetDrag();
      if (wasDrag) suppressClickUntil = performance.now() + 450;
      scheduleAutoplay();
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', (event) => {
      if (!tracking || event.pointerId !== pointerId) return;
      resetDrag();
      scheduleAutoplay();
    });

    track.addEventListener(
      'click',
      (event) => {
        if (performance.now() >= suppressClickUntil) return;
        const trigger = event.target.closest('[data-social-video-open]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = 0;
      },
      true
    );

    const stopModalVideo = () => {
      const currentVideo = modalHost.querySelector('video');
      if (currentVideo) currentVideo.pause();
      modalHost.replaceChildren();
    };

    const getPlayableIndex = (index, step = 1) => {
      if (!playableIndices.length) return null;
      let candidate = (index + items.length) % items.length;

      for (let count = 0; count < items.length; count += 1) {
        if (playableIndices.includes(candidate)) return candidate;
        candidate = (candidate + step + items.length) % items.length;
      }

      return playableIndices[0];
    };

    const renderModalVideo = (index, step = 1) => {
      const resolvedIndex = getPlayableIndex(index, step);
      if (resolvedIndex === null) return;

      modalIndex = resolvedIndex;
      const template = items[modalIndex].querySelector('template[data-modal-template]');
      if (!template) return;

      stopModalVideo();
      modalHost.appendChild(template.content.cloneNode(true));

      const video = modalHost.querySelector('video');
      if (video) {
        video.muted = false;
        video.controls = true;
        video.playsInline = true;
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }

      if (modalCounter) {
        const playablePosition = playableIndices.indexOf(modalIndex);
        modalCounter.textContent = `${playablePosition + 1} / ${playableIndices.length}`;
      }
    };

    const openModal = (index, trigger) => {
      if (!playableIndices.includes(index)) return;

      clearAutoplay();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      returnFocus = trigger;
      renderModalVideo(index);

      requestAnimationFrame(() => modal.querySelector('.pedrocchi-social-video-carousel__modal-close')?.focus());
    };

    const closeModal = () => {
      stopModalVideo();
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = previousBodyOverflow;
      returnFocus?.focus();
      returnFocus = null;
      scheduleAutoplay();
    };

    openButtons.forEach((button) => {
      const item = button.closest('[data-social-video-item]');
      const index = items.indexOf(item);
      button.addEventListener('click', () => openModal(index, button));
    });

    modalPrevious?.addEventListener('click', () => renderModalVideo(modalIndex - 1, -1));
    modalNext?.addEventListener('click', () => renderModalVideo(modalIndex + 1, 1));
    modalCloseButtons.forEach((button) => button.addEventListener('click', closeModal));

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        renderModalVideo(modalIndex - 1, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        renderModalVideo(modalIndex + 1, 1);
      }
    });

    document.addEventListener('visibilitychange', scheduleAutoplay);
    if (typeof reducedMotion.addEventListener === 'function') reducedMotion.addEventListener('change', scheduleAutoplay);

    scheduleAutoplay();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-social-video-carousel]').forEach(initSocialVideoCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
